/**
 * NeverBounce Former-Member background campaign — admin service wrapper.
 *
 * Talks ONLY to the admin-gated `neverbounce-campaign-admin` edge function (verify_jwt + server-side
 * admin/superadmin re-check). The UI NEVER calls NeverBounce directly and NEVER computes counts
 * client-side — every number comes from the server RPCs. Spending is not initiated here; the isolated
 * campaign worker (its own secret) performs billable starts once a pilot is approved.
 */

import { supabase } from "./supabase";

async function invoke<T = unknown>(body: Record<string, unknown>): Promise<T> {
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session) {
    throw new Error("No active session");
  }
  const { data, error } = await supabase.functions.invoke(
    "neverbounce-campaign-admin",
    {
      body,
      headers: { Authorization: `Bearer ${session.session.access_token}` },
    },
  );
  if (error) {
    throw new Error(error.message);
  }
  if (data && data.success === false) {
    throw new Error(data.error ?? "Request failed");
  }
  return data as T;
}

export interface CampaignStatus {
  id: string;
  state: string;
  campaign_type?: string;
  criteria_version?: string;
  cohort_fingerprint?: string | null;
  snapshot_distinct_count?: number | null;
  snapshot_exhausted?: boolean;
  activated?: boolean;
  ttl_days?: number;
  batch_size?: number;
  auto_suppress?: boolean;
  jobs_created?: number;
  jobs_completed?: number;
  jobs_failed?: number;
  results_imported?: number;
  skip_counts?: Record<string, number>;
  verdict_counts?: Record<string, number>;
  member_state_counts?: Record<string, number>;
  job_state_counts?: Record<string, number>;
  last_error?: string | null;
}

export interface CreditsInfo {
  paid_remaining: number;
  free_remaining: number;
}

export const neverbounceCampaignService = {
  create: (
    opts: {
      ttl_days?: number;
      batch_size?: number;
    } = {},
  ) =>
    invoke<{ campaign_id: string; state: string }>({
      action: "create",
      ...opts,
    }),

  snapshotPage: (
    campaignId: string,
    afterEmail?: string | null,
    limit = 10000,
  ) =>
    invoke<{ scanned: number; last_email: string | null; done: boolean }>({
      action: "snapshot_page",
      campaign_id: campaignId,
      after_email: afterEmail ?? null,
      limit,
    }),

  finalizeSnapshot: (campaignId: string) =>
    invoke<{
      ok?: boolean;
      reason?: string;
      detail?: string;
      snapshot_distinct_count?: number;
      cohort_fingerprint?: string;
      state: string;
    }>({ action: "finalize_snapshot", campaign_id: campaignId }),

  status: (campaignId: string) =>
    invoke<{ status: CampaignStatus; credits: CreditsInfo | null }>({
      action: "status",
      campaign_id: campaignId,
    }),

  startRun: (
    campaignId: string,
    expectedFingerprint: string,
  ) =>
    invoke<{ ok: boolean; reason?: string; state?: string }>({
      action: "start_run",
      campaign_id: campaignId,
      expected_fingerprint: expectedFingerprint,
    }),

  pause: (campaignId: string, reason?: string) =>
    invoke<{ ok: boolean; state?: string }>({
      action: "pause",
      campaign_id: campaignId,
      reason,
    }),
  resume: (campaignId: string) =>
    invoke<{ ok: boolean; state?: string }>({
      action: "resume",
      campaign_id: campaignId,
    }),
  cancel: (campaignId: string, reason?: string) =>
    invoke<{ ok: boolean; state?: string }>({
      action: "cancel",
      campaign_id: campaignId,
      reason,
    }),

  verdictReport: (campaignId: string) =>
    invoke<{
      verdict_counts: Record<string, number>;
      resolved_members: number;
    }>({ action: "verdict_report", campaign_id: campaignId }),

  exportPage: (campaignId: string, afterSeq = 0, limit = 5000) =>
    invoke<{
      rows: Array<{
        seq: number;
        email_lower: string;
        member_state: string;
        skip_reason: string | null;
        verdict: string | null;
        provider_result: string | null;
      }>;
    }>({
      action: "export_page",
      campaign_id: campaignId,
      after_seq: afterSeq,
      limit,
    }),
};

export default neverbounceCampaignService;
